// core/UIStateProvider.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface Notification {
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
    timestamp: Date;
}

interface UIStateContextValue {
    // Notifications
    notifications: Notification[];
    addNotification: (type: Notification['type'], message: string, timeout?: number) => void;
    clearNotification: (id: string) => void;
}

const UIStateContext = createContext<UIStateContextValue | null>(null);

export const useUIState = () => {
    const context = useContext(UIStateContext);
    if (!context) {
        throw new Error('useUIState must be used within UIStateProvider');
    }
    return context;
};

interface UIStateProviderProps {
    children: ReactNode;
}

export const UIStateProvider: React.FC<UIStateProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const addNotification = useCallback(
        (type: Notification['type'], message: string, timeout: number = 5000) => {
            const notification: Notification = {
                id: Date.now().toString() + Math.random(),
                type,
                message,
                timestamp: new Date(),
            };

            setNotifications((prev) => [...prev, notification]);

            if (timeout > 0) {
                setTimeout(() => {
                    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
                }, timeout);
            }
        },
        []
    );

    const clearNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const value: UIStateContextValue = {
        notifications,
        addNotification,
        clearNotification
    };

    return (
        <UIStateContext.Provider value={value}>
            {children}
        </UIStateContext.Provider>
    );
};